// Riptide pull-to-cell helper. Pure. No React, no Three.js.
//
// ── Codex ──
//   "Target water site PULLS IN an aboveground unit it's ADJACENT TO."
//
// Two-stage op: the player first picks a water SITE (filter
// { type: 'Site', element: 'water' } on the standard target halo), then
// the runtime pulls one aboveground unit standing on a cardinally
// adjacent cell INTO the site's cell.
//
// "Aboveground" is the Sorcery surface sense. A unit standing atop the
// board, not submerged (underwater) or burrowed (underground). The
// region read is supplied by the caller (regionsAtCell) so this stays a
// pure board-geometry helper.

import { countsAsUnit } from '../../core/typeline';

/**
 * Cells holding an aboveground unit cardinally adjacent to the picked
 * site, so the runtime can offer them as the pull-source halo.
 *
 * @param siteCell    { col, row }. The chosen water site's cell
 * @param meshes      host.meshes Map
 * @param cellAt      (x, z) => { col, row } | null
 * @param totalCols / totalRows  grid dimensions
 * @param isSurfaceUnitAt (col, row, unitCard) => boolean. True iff the
 *        unit occupies the surface region of that cell (not submerged)
 * @returns {{ card, cell: { col, row } }[]} pullable units (one entry per unit)
 */
export function pullableAbovegroundUnitCells(siteCell, meshes, cellAt, totalCols, totalRows, isSurfaceUnitAt) {
  if (!siteCell) return [];
  const { col, row } = siteCell;
  const adjacent = new Set([
    `${col + 1},${row}`,
    `${col - 1},${row}`,
    `${col},${row + 1}`,
    `${col},${row - 1}`,
  ]);
  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !countsAsUnit(card)) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    if (cell.col < 0 || cell.col >= totalCols || cell.row < 0 || cell.row >= totalRows) continue;
    if (!adjacent.has(`${cell.col},${cell.row}`)) continue;
    if (!isSurfaceUnitAt(cell.col, cell.row, card)) continue;
    out.push({ card, cell });
  }
  return out;
}
